"use client";
import * as React from "react";
import { FormInput } from "@/HomeFeatures/Contact/FormInput";
import { SelectInput } from "@/HomeFeatures/Contact/SelectInput";
import { PhoneInput } from "@/HomeFeatures/Contact/PhoneInput";
import dynamic from "next/dynamic";
import type ReCAPTCHAType from "react-google-recaptcha";

const ReCAPTCHA = dynamic(
  async () => {
    const mod = await import("react-google-recaptcha");
    return mod.default;
  },
  { ssr: false },
) as unknown as typeof ReCAPTCHAType;

import { FormProps, CareerFormData } from "@/lib/Types";
import clsx from "clsx";
import { FileUpload } from "@/HomeFeatures/Contact/FileUpload";
import { useRouter, usePathname } from "next/navigation";
import {
  getAWSsignUrl,
  uploadFileToS3,
  saveCareerForm,
  getCountryList,
} from "@/lib/ApiClient";
import { normalizeFileType } from "@/lib/Utility";
import CustomSubmitButton from "@/components/CustomSubmitButton";
import { ProfileArray } from "./ProfileSelectArray";

export const initialData: CareerFormData = {
  name: "",
  email: "",
  phone: "",
  countryCode: "+1",
  skypeWhatsapp: "",
  profile: "",
  experience: "",
  message: "",
  sendMeNda: false,
  file: "",
  gRecaptchaResponse: "",
};

const Form: React.FC<FormProps> = ({ variant = "light" }) => {
  const pathname = usePathname();
  const router = useRouter();

  let lastSegment = "/";

  if (pathname && pathname !== "/") {
    const segments = pathname.split("/").filter(Boolean);
    lastSegment = segments[segments.length - 1] || "/";
  }

  const recaptchaRef = React.useRef<ReCAPTCHAType | null>(null);

  const [formData, setFormData] = React.useState<CareerFormData>(initialData);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [recaptchaToken, setRecaptchaToken] = React.useState("");
  const [selectedCountry, setSelectedCountry] = React.useState({
    slug: "US",
    code: "+1",
  });

  const [countryListArray, setCountryListArray] = React.useState<
    { slug: string; code: string; countryName: string }[]
  >([]);

  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  const handleInputChange = React.useCallback(
    (field: string) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
        const value = e?.target?.value ?? e;
        setFormData((prev) => ({ ...prev, [field]: value }));
      },
    [],
  );

  //Validate Contact Form
  const validate = React.useCallback(async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name?.trim()) newErrors.name = "Full Name is required";

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.countryCode?.trim())
      newErrors.countryCode = "Country code is required";

    if (!formData.phone?.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.profile?.trim()) newErrors.profile = "Profile is required";

    if (!formData.experience?.trim())
      newErrors.experience = "Experience is required";

    if (!formData.message?.trim()) newErrors.message = "Message is required";

    // if (!formData.sendMeNda) newErrors.sendMeNda = "Please Check For Non-Disclosure Agreement";

    // Captcha validation only if site key configured
    if (recaptchaSiteKey) {
      const token = recaptchaToken;
      if (!token) {
        newErrors.gRecaptchaResponse = "Please complete the captcha.";
      }
    }

    return newErrors;
  }, [formData, recaptchaToken, recaptchaSiteKey]);

  //Submit Contact Form
  const handleSubmit = React.useCallback(
    async (e: React.FormEvent) => {
      try {
        e.preventDefault();

        if (submitting) return;
        setSubmitError(null);

        const validationErrors = await validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length !== 0) {
          return;
        }

        setSubmitting(true);
        let filePreview = "";
        if (formData?.file && (formData.file as any).size) {
          const fileObj = formData.file as any;
          const fileType = normalizeFileType(
            fileObj.type || fileObj.name || "",
          );
          if (!fileType) {
            throw new Error(
              "Unsupported file format. Allowed: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV, JSON, RTF, ODT, ODS, ODP, JPG, PNG, WEBP.",
            );
          }
          const signedUrlResp: any = await getAWSsignUrl(fileType);
          if (!signedUrlResp?.url)
            throw new Error("Failed to obtain upload URL");
          await uploadFileToS3(signedUrlResp.url, fileObj);
          filePreview = signedUrlResp.preview || signedUrlResp.url || "";
        }

        const token = recaptchaToken || "";

        const payload: any = {
          ...formData,
          file: filePreview,
          gRecaptchaResponse: token,
        };

        const saveDataResp = await saveCareerForm(payload);

        if (saveDataResp?.success) {
          setFormData(initialData);
          try {
            recaptchaRef.current?.reset();
            setRecaptchaToken("");
          } catch (e) {
            console.log(e);
          }
          router.push(
            `/thank-you?type=${lastSegment === "/" ? "contact-form" : lastSegment}`,
          );
        } else {
          throw new Error(saveDataResp?.message || "Failed to save enquiry");
        }
      } catch (err: any) {
        setSubmitError(
          err?.message || "Something went wrong while submitting the form.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [formData, recaptchaToken, submitting, validate, router, lastSegment],
  );

  //Fetch Country List
  const fetchCountryList = React.useCallback(async (): Promise<void> => {
    try {
      const data: any = await getCountryList();
      const items = data?.items ?? [];
      setCountryListArray(items);
    } catch (err) {
      console.error(
        err instanceof Error ? err.message : "Failed to fetch country list",
      );
    }
  }, []);

  React.useEffect(() => {
    fetchCountryList();
  }, [fetchCountryList]);

  return (
    <form
      onSubmit={handleSubmit}
      className={clsx(
        "flex flex-col text-center w-full px-0 lg:px-5",
        variant === "dark"
          ? "bg-white/5 border border-white/20 backdrop-blur-[40px] rounded-2xl text-white"
          : "bg-white",
      )}
    >
      {variant === "dark" && (
        <span className="text-[clamp(1.125rem,0.6262rem+1.0393vw,2.25rem)] py-[15px] border-b border-solid border-white/20 font-medium text-left px-3 lg:px-8">
          Share Your Requirement
        </span>
      )}

      <div className="w-full max-md:max-w-full px-4 pb-4 pt-4 xl:p-8 text-left">
        <div className="w-full max-md:max-w-full">
          <div
            className={clsx(
              "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
              variant === "dark" ? "text-white" : "text-zinc-600",
            )}
          >
            <FormInput
              label="Candidate Name"
              variant={variant}
              value={formData.name}
              onChange={handleInputChange("name")}
            />
            {errors.name && (
              <div className="text-red-500 text-xs mt-1">{errors.name}</div>
            )}
          </div>

          <div className="flex flex-1 flex-col">
            <FormInput
              label="Email Address"
              variant={variant}
              value={formData.email}
              onChange={handleInputChange("email")}
            />
            {errors.email && (
              <div className="text-red-500 text-xs mt-1">{errors.email}</div>
            )}
          </div>
        </div>

        <div
          className={clsx(
            "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
            variant === "dark" ? "text-white" : "text-zinc-600",
          )}
        >
          <div className="flex flex-1 flex-col">
            <PhoneInput
              variant={variant}
              selectedCountry={selectedCountry}
              countryListArray={countryListArray}
              setSelectedCountry={setSelectedCountry}
              phone={formData.phone}
              setFormData={setFormData}
            />
            {errors.phone && (
              <div className="text-red-500 text-xs mt-1">{errors.phone}</div>
            )}
          </div>

          <div className="flex flex-1 flex-col">
            <SelectInput
              label="Select Profile"
              type="select"
              value={formData.profile}
              onChange={handleInputChange("profile")}
              options={ProfileArray}
            />
            {errors.profile && (
              <div className="text-red-500 text-xs mt-1">{errors.profile}</div>
            )}
          </div>
        </div>

        <div
          className={clsx(
            "flex 2xl:flex-row flex-col flex-wrap 2xl:gap-6 items-start w-full text-base max-md:max-w-full",
            variant === "dark" ? "text-white" : "text-zinc-600",
          )}
        >
          <div className="flex flex-1 flex-col">
            <FormInput
              label="Experience (YRS/MTHS)"
              className={clsx("min-h-[59px]")}
              variant={variant}
              value={formData.experience}
              onChange={handleInputChange("experience")}
            />
            {errors.experience && (
              <div className="text-red-500 text-xs mt-1">
                {errors.experience}
              </div>
            )}
          </div>
          <div className="flex flex-1 flex-col">
            <FormInput
              label="MS Teams ID / WhatsApp No."
              className={clsx("min-h-[59px]")}
              variant={variant}
              value={formData.skypeWhatsapp}
              onChange={handleInputChange("skypeWhatsapp")}
            />
            {errors.skypeWhatsapp && (
              <div className="text-red-500 text-xs mt-1">
                {errors.skypeWhatsapp}
              </div>
            )}
          </div>
        </div>

        <div className="w-full">
          <label
            className={clsx(
              "block text-base",
              variant === "dark" ? "text-white" : "text-zinc-600",
            )}
          >
            <textarea
              placeholder="Message"
              value={formData.message}
              onChange={handleInputChange("message")}
              className={clsx(
                "w-full border-b border-solid bg-transparent outline-none min-h-[120px] resize-vertical",
                variant === "dark"
                  ? "border-white/20 text-white placeholder-white/70"
                  : "border-b-[color:var(--Stroke-2,#E3E3E3)] 2xl:pt-9 pb-3.5 text-zinc-600",
              )}
              aria-label="Message"
            />
          </label>
          {errors.message && (
            <div className="text-red-500 text-xs mt-1">{errors.message}</div>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-7 2xl:gap-10 justify-between items-center w-full font-semibold">
          <div className="fit-content">
            {variant === "light" && (
              <FileUpload
                title={"ADD RESUME"}
                variant={variant}
                onUploadSuccess={(fileInfo: any) => {
                  setFormData((prev) => ({
                    ...prev,
                    file: fileInfo,
                  }));
                }}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-start my-4">
        {recaptchaSiteKey ? (
          <>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              onChange={(token: string | null) => {
                setRecaptchaToken(token || "");
                setErrors((prev) => {
                  const copy = { ...prev };
                  delete copy.gRecaptchaResponse;
                  return copy;
                });
              }}
              onExpired={() => {
                setRecaptchaToken("");
              }}
            />

            {errors.gRecaptchaResponse && (
              <div className="text-red-500 text-xs mt-1">
                {errors.gRecaptchaResponse}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-yellow-600">
            reCAPTCHA not configured. Please set NEXT_PUBLIC_RECAPTCHA_SITE_KEY
            environment variable.
          </div>
        )}
      </div>

      {submitError && (
        <div className="text-red-500 text-sm mb-3">{submitError}</div>
      )}

      <CustomSubmitButton title={"Submit Your Resume"} />
    </form>
  );
};

export default Form;
